import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Button, Skeleton, Tooltip } from '@heroui/react';
import { DataGrid, type DataGridColumn } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { DeclarationRow } from '../../dividendsMeta';
import { useDeclarationHistory } from './useDeclarationHistory';

type DeclarationHistoryProps = {
    rows: DeclarationRow[];
    /** The history feed is still in flight — no row count is known yet. */
    isLoading: boolean;
};

/**
 * Past declarations, newest first.
 *
 * A HeroUI Pro `DataGrid`, like every register on `/accounting`. It renders a
 * real `<table>` underneath, with the declaration date as the row header; its
 * accessible name is `aria-label` (DataGrid has no `<caption>` slot).
 *
 * A preview never appears here — a declaration exists only once its entry has been
 * posted. The empty state says exactly that, and it is shown **only once the feed
 * has resolved**: an empty list while the query is in flight means "not known
 * yet", not "none", and announcing "no distributions" over a user's own posted
 * entries is the kind of confident falsehood this surface is built to avoid.
 *
 * Each row carries the **15th-of-next-month due date** — when both the CIT and the
 * withheld dividend tax fall due — as information. Nothing here files anything.
 */
export const DeclarationHistory: React.FC<DeclarationHistoryProps> = ({
    rows,
    isLoading,
}) => {
    const { t } = useTranslation('pages');
    const { onOpenEntry } = useDeclarationHistory();

    const columns = React.useMemo<DataGridColumn<DeclarationRow>[]>(
        () => [
            {
                id: 'date',
                header: t('dividends.history.columns.date'),
                isRowHeader: true,
                minWidth: 130,
                cell: (row) => (
                    <span className="flex items-center gap-2">
                        <span className="whitespace-nowrap font-medium tabular-nums text-foreground">
                            {row.dateText}
                        </span>
                        {/* Declared, but its entry is still a draft: none of the
                            figures on this row is in the ledger, and none of
                            them is in the summary above either. */}
                        {row.isUnposted ? (
                            <span
                                className="inline-flex w-fit items-center rounded-lg bg-warning-soft px-2 py-0.5 text-xs font-medium text-warning-soft-foreground"
                                {...getNodeTestIdProp(
                                    `dividends-history-unposted-${row.id}`,
                                )}
                            >
                                {t('dividends.history.unposted')}
                            </span>
                        ) : null}
                    </span>
                ),
            },
            {
                id: 'shareholders',
                header: t('dividends.history.columns.shareholders'),
                align: 'end',
                minWidth: 130,
                cell: (row) => (
                    <span className="tabular-nums">{row.shareholderCount}</span>
                ),
            },
            {
                id: 'charge',
                header: t('dividends.history.columns.charge'),
                align: 'end',
                minWidth: 130,
                cell: (row) => (
                    <span className="whitespace-nowrap tabular-nums">
                        {row.chargeText}
                    </span>
                ),
            },
            {
                id: 'cit',
                header: t('dividends.history.columns.cit'),
                align: 'end',
                minWidth: 120,
                cell: (row) => (
                    <span className="whitespace-nowrap tabular-nums">
                        {row.citText}
                    </span>
                ),
            },
            {
                id: 'net',
                header: t('dividends.history.columns.net'),
                align: 'end',
                minWidth: 130,
                cell: (row) => (
                    <span className="whitespace-nowrap font-medium tabular-nums text-foreground">
                        {row.netText}
                    </span>
                ),
            },
            {
                id: 'dueDate',
                header: t('dividends.history.columns.dueDate'),
                minWidth: 130,
                cell: (row) => (
                    <span className="whitespace-nowrap tabular-nums text-muted">
                        {row.dueDateText}
                    </span>
                ),
            },
            {
                id: 'actions',
                header: (
                    <span className="sr-only">
                        {t('dividends.columns.actions')}
                    </span>
                ),
                align: 'end',
                width: 72,
                // The accessible name lives in a visually hidden span, not
                // `aria-label`: HeroUI's Tooltip wrapper drops an `aria-label`
                // set on its trigger Button, leaving an icon-only trigger
                // nameless.
                cell: (row) => (
                    <span
                        className="flex items-center justify-end"
                        data-row-actions=""
                    >
                        <Tooltip delay={0}>
                            <Button
                                isIconOnly
                                onPress={() =>
                                    onOpenEntry(row.journalEntryId, row.date)
                                }
                                size="sm"
                                variant="ghost"
                                {...getNodeTestIdProp(
                                    `dividends-history-open-${row.id}`,
                                )}
                            >
                                <Icon
                                    aria-hidden
                                    icon="gravity-ui:arrow-up-right-from-square"
                                />
                                <span className="sr-only">
                                    {t('dividends.history.openEntryNamed', {
                                        date: row.dateText,
                                    })}
                                </span>
                            </Button>
                            <Tooltip.Content>
                                <p>{t('dividends.history.openEntry')}</p>
                            </Tooltip.Content>
                        </Tooltip>
                    </span>
                ),
            },
        ],
        [onOpenEntry, t],
    );

    if (isLoading) {
        return (
            <div
                className="flex flex-col gap-2 rounded-2xl border border-separator bg-surface p-4"
                {...getNodeTestIdProp('dividends-history-loading')}
            >
                {[0, 1].map((row) => (
                    <Skeleton className="h-9 w-full" key={row} />
                ))}
            </div>
        );
    }

    if (rows.length === 0) {
        return (
            <div
                className="flex flex-col items-center gap-2 rounded-2xl border border-separator bg-surface px-6 py-10 text-center"
                {...getNodeTestIdProp('dividends-history-empty')}
            >
                <Icon
                    aria-hidden
                    className="size-7 text-muted"
                    icon="gravity-ui:calendar"
                />
                <span className="text-sm font-medium text-foreground">
                    {t('dividends.history.empty.title')}
                </span>
                <span className="max-w-sm text-sm text-muted">
                    {t('dividends.history.empty.body')}
                </span>
            </div>
        );
    }

    // `contents` wrapper: `DataGrid` forwards no unknown props, so the list's
    // testid has nowhere to land on the grid itself.
    return (
        <div
            className="contents"
            {...getNodeTestIdProp('dividends-history-table')}
        >
            <DataGrid<DeclarationRow>
                aria-label={t('dividends.history.tableAria')}
                className="ledger-data-grid"
                columns={columns}
                contentClassName="min-w-[720px]"
                data={rows}
                getRowId={(row) => row.id}
            />
        </div>
    );
};
